/* LT-ProvSums-01 — Provisional Sums Library admin list page.
 * Mirrors PrelimLibrary structure (search + show-archived toggle + list).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp } from "../lib/format";
import { PageHeader, Card, PrimaryButton, EmptyState } from "../components/UI";

const cls = (...a) => a.filter(Boolean).join(" ");

export default function ProvsumLibrary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/provsum-library", { params: { include_archived: showArchived } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load Pro Sums library"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => (it.description || "").toLowerCase().includes(needle));
  }, [items, q]);

  return (
    <div className="app-container py-8" data-testid="provsum-library-page">
      <PageHeader
        title="Pro Sums Library"
        subtitle="Master list of standard provisional sum descriptions used to seed new tenders."
        actions={
          <PrimaryButton
            icon={Plus}
            onClick={() => navigate("/provsum-library/new")}
            data-testid="provsum-library-new-btn"
          >
            New description
          </PrimaryButton>
        }
      />

      <Card>
        <div className="p-4 space-y-3">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={13} className="absolute left-2.5 top-2.5 text-ink-4" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search descriptions…"
              className="w-full h-8 pl-8 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ fontSize: 12.5 }}
              data-testid="provsum-library-search"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-ink-3" style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded"
              data-testid="provsum-library-show-archived"
            />
            Show archived
          </label>
        </div>

        {loading ? (
          <div className="text-ink-4 py-8 text-center" style={{ fontSize: 12 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title={q ? "No matches" : "No descriptions yet"}
            hint={q ? "Try a different search." : "Add the first description to start populating new tenders."}
          />
        ) : (
          <table className="w-full" style={{ fontSize: 12.5 }}>
            <thead>
              <tr className="border-b border-line text-left">
                <th className="col-head py-2" style={{ fontSize: 10, width: 60 }}>Order</th>
                <th className="col-head py-2" style={{ fontSize: 10 }}>Description</th>
                <th className="col-head py-2 text-right" style={{ fontSize: 10, width: 140 }}>Default value</th>
                <th className="col-head py-2" style={{ fontSize: 10, width: 90 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr
                  key={it.id}
                  className={cls(
                    "border-b border-line/60 last:border-0 cursor-pointer hover:bg-surface-2/40",
                    !it.is_active && "opacity-60",
                  )}
                  onClick={() => navigate(`/provsum-library/${it.id}`)}
                  data-testid={`provsum-library-row-${it.id}`}
                >
                  <td className="py-2 text-ink-4" style={{ fontSize: 11 }}>{it.display_order}</td>
                  <td className="py-2 text-ink">{it.description}</td>
                  <td className="py-2 text-right text-ink-2 tabular-nums">
                    {it.default_value > 0 ? gbp(it.default_value) : <span className="text-ink-4">£ 0.00</span>}
                  </td>
                  <td className="py-2">
                    {it.is_active ? (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded"
                        style={{ fontSize: 10, background: "#E8EFE9", color: "#2F4F3E" }}
                      >
                        ACTIVE
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded"
                        style={{ fontSize: 10, background: "#F1ECEA", color: "#787068" }}
                      >
                        ARCHIVED
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </Card>
    </div>
  );
}
